import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { CheckMarkIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import SecureImageViewCard from '../../../elements/secure-image-view-card'
import GeneralPhilosophyInstructions from './child-philosophy-instructions'

export type EnrollmentChildHostFamilyDataType = Pick<
  Enrollment,
  | 'childFirstName'
  | 'childLastName'
  | 'childHostFamilyLetter'
  | 'parentAcceptGeneralPhilosophySignedAt'
  | 'childAcceptGeneralPhilosophySignedAt'
  | 'acceptMedicalGeneralTermsSignedAt'
> & {
  secureFamilyImages: Pick<
    Enrollment['secureFamilyImages'][number],
    'id' | 'uploadId' | 'name' | 'size' | 'caption' | 'type'
  >[]
}

type Props = {
  enrollment: EnrollmentChildHostFamilyDataType
}

const SignedInformationBox = ({ date, text }: { date: Date; text: string }) => {
  return (
    <InnerBox className="rounded-lg p-4 flex flex-row gap-3 ">
      <div className="flex items-center bg-green-light w-fit h-fit p-1 rounded-full ">
        <CheckMarkIcon color={tailwindColors.green.DEFAULT} height="20" width="20" />
      </div>
      <div className="flex flex-col gap-2">
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {text}
        </Typography>
        <Badge
          variant="success"
          text={`Signé électroniquement le ${DateFormatter.ddMMYYYY_HHMM(date)}`}
        />
      </div>
    </InnerBox>
  )
}

const ChildHostFamilyData = ({ enrollment }: Props) => {
  const { childFirstName, childLastName } = enrollment
  return (
    <div className="flex flex-col gap-3.5">
      <InnerBox className="rounded-lg p-4 flex flex-col gap-3">
        <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
          Lettre du participant à sa famille hôtesse
        </Typography>
        <InnerBox className="rounded-lg bg-white-hover p-4 mobile:p-2">
          <Typography
            variant="label"
            color={tailwindColors.grey.lighter}
            className="whitespace-pre-line"
          >
            {enrollment.childHostFamilyLetter || 'Aucune lettre renseignée'}
          </Typography>
        </InnerBox>
      </InnerBox>
      <InnerBox className="rounded-lg p-4  flex flex-col gap-3">
        <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
          Quelques photos de votre famille
        </Typography>
        <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
          {enrollment.secureFamilyImages?.length > 0 ? (
            enrollment.secureFamilyImages.map((picture) => (
              <SecureImageViewCard key={picture.id} picture={picture} />
            ))
          ) : (
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Aucune photo renseignée
            </Typography>
          )}
        </div>
      </InnerBox>
      <InnerBox className="rounded-lg p-4 flex flex-col gap-3">
        <GeneralPhilosophyInstructions />
        <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
          {enrollment.parentAcceptGeneralPhilosophySignedAt && (
            <SignedInformationBox
              text="Nous, parents, nous engageons à assumer la responsabilité financière si notre
                fils/fille doit être rapatrié(e) avant la fin du séjour pour mauvaise conduite.
                Nous avons pris connaissance et acceptons le contenu du guide LEC et de ce dossier
                complémentaire, et certifions n’avoir omis aucune information importante."
              date={enrollment.parentAcceptGeneralPhilosophySignedAt}
            />
          )}
          {enrollment.childAcceptGeneralPhilosophySignedAt && (
            <SignedInformationBox
              text={`Moi, ${childFirstName ? StringFormatter.capitalize(childFirstName) : '-'}
                ${childLastName?.toUpperCase()}, m’engage à respecter le code de
                bonne conduite ci-dessus.`}
              date={enrollment.childAcceptGeneralPhilosophySignedAt}
            />
          )}
        </div>
      </InnerBox>
      <InnerBox className="rounded-lg p-4 flex flex-col gap-3">
        <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
          Décharge médicale
        </Typography>
        {enrollment.acceptMedicalGeneralTermsSignedAt && (
          <SignedInformationBox
            text={`Je soussigné, père, mère ou tuteur de ${toFullName(enrollment.childFirstName, enrollment.childLastName)}, autorise LEC, 89 avenue de
            Villiers à Paris 75017, ainsi que ses collaborateurs et représentants français et
            étrangers (et la famille d’accueil) à agir en mon nom en cas d’urgence (maladie,
            accident, opération chirurgicale, - y compris anesthésie -, hospitalisation) pendant la
            durée de son séjour à l’étranger. Cette décharge sera présentée au médecin, dentiste,
            chirurgien ou directeur d’hôpital le cas échéant.`}
            date={enrollment.acceptMedicalGeneralTermsSignedAt}
          />
        )}
      </InnerBox>
    </div>
  )
}

export default ChildHostFamilyData
